import type {
  Project,
  ProjectTable,
  TableColumnDefinition,
  TableFieldType,
} from '@/lib/types'
import type { PaginationClass } from '@/shared'
import { isInteger } from 'lodash'

export type IColumn = {
  name: string
  type: string
  nullable: boolean
}

export type SetTableNameToPagination = React.Dispatch<
  React.SetStateAction<Record<string, PaginationClass>>
>

export type SetTableName = React.Dispatch<React.SetStateAction<string>>

// 数据自动生成列
export const AUTO_GENERATED_FIELDS = ['created_at', 'updated_at', 'id']

// 字段类型映射表（DB 类型 => JSON Schema 类型 + x-component）
export const COLUMN_TYPE_MAP: Record<
  string,
  { type: string; component: string }
> = {
  integer: {
    type: 'number',
    component: 'InputNumber',
  },
  int: {
    type: 'number',
    component: 'InputNumber',
  },
  numeric: {
    type: 'number',
    component: 'InputNumber',
  },
  number: {
    type: 'number',
    component: 'InputNumber',
  },
  float: {
    type: 'number',
    component: 'InputNumber',
  },
  text: { type: 'string', component: 'Input' },
  web_url: { type: 'string', component: 'Input' },
  address: {
    type: 'string',
    component: 'Input',
  },
  media_url: {
    type: 'string',
    component: 'Uploader',
  },
  file_url: {
    type: 'string',
    component: 'Uploader',
  },
  varchar: { type: 'string', component: 'Input' },
  id: { type: 'string', component: 'Input' },
  uuid: { type: 'string', component: 'Input' },
  boolean: {
    type: 'boolean',
    component: 'Switch',
  },
  checkbox: {
    type: 'boolean',
    component: 'Switch',
  },
  date: {
    type: 'string',
    component: 'DatePicker',
  },
  timestamp: {
    type: 'string',
    component: 'DateTimePicker',
  },
  'timestamp with time zone': {
    type: 'string',
    component: 'DateTimePicker',
  },
  'timestamp without time zone': {
    type: 'string',
    component: 'DateTimePicker',
  },
  tag: {
    type: 'string',
    component: 'SelectV2',
  },
  rich_text: { type: 'string', component: 'RichText' },
}

// 行数据校验
export const validateColumnType = (
  value: unknown,
  col: ProjectTable['columns'][0],
) => {
  switch (col.type) {
    case 'number': {
      if (Number.isNaN(value)) return false
      break
    }
    case 'integer':
    case 'id': {
      if (Number.isNaN(value) || !isInteger(Number(value))) {
        return false
      }
    }
  }
  return true
}

// 将字符串类型解析为 db 数据
export const parseData = (value: unknown, type: TableFieldType) => {
  if (value === null || value === undefined) return undefined
  if (type === 'number') {
    const num = Number(value)
    return Number.isNaN(num) ? null : num
  }
  if (type === 'checkbox') {
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      const lower = value.toLowerCase()
      if (['true', '1'].includes(lower)) return true
      if (['false', '0'].includes(lower)) return false
    }
    if (typeof value === 'number') return value === 1
    return false // 默认 false
  }
  return value
}

export type IColumnType = keyof typeof COLUMN_TYPE_MAP

// 通用转换函数
export const convertTableToFormilySchema = (
  table: TableColumnDefinition,
  t: any,
) => {
  const properties: Record<string, any> = {}
  for (const col of table?.field_list || []) {
    const { type, db_name, required, ...rest } = col
    const key = col.type.toLowerCase()
    const mapping = COLUMN_TYPE_MAP[key] ?? {
      type: 'string',
      component: 'Input',
    }

    properties[col.db_name] = {
      type: mapping.type,
      title: col.ui_name,
      'x-validator': [
        {
          required: col.required,
          message: t('required'),
        },
      ],
      'x-decorator': 'FormItem',
      'x-column-type': col.type,
      'x-column-props': rest,
      'x-component': mapping.component,
      enum:
        col.type === 'tag'
          ? col.tag_values?.map((d) => ({
              label: d,
              value: d,
            }))
          : undefined,
    }
  }

  return {
    type: 'object',
    properties,
  }
}
